import React, { useContext, useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  TextField,
  Card,
  CardContent,
  Typography,
  Box,
  InputAdornment,
  Container,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import {
  evaluateTestDesignReadiness,
  validateUserInput,
  validRefinedUserStory,
} from "../../../services/webServices";
import { ReactComponent as GenerateButton } from "../../../images/generateButton.svg";
import { ReactComponent as ImportIcon } from "../../../images/importIcon.svg";
import SearchIcon from "@mui/icons-material/Search";
import {
  generateFSD,
  validCandidateUserStory,
  modifyUserStoryData,
} from "../../../services/webServices";
// import userStoryInvest from "../../../../../QMentisAIBackend/src/utils/userStoryValidation";
import {
  LoadingModalContext,
  MessagePopupContext,
} from "../../main-pages/ServicePage";
import {
  TestCaseFlowDataContext,
  TestCaseLoaderStateContext,
} from "../../main-pages/TestcaseGenerationPage";
import { ReactComponent as AutomateIcon } from "../../../images/automateIcon.svg";
import FullscreenToggle from "../../custom-components/FullScreenToggle";
import SessionExpired from "../../popup-components/SessionExpiredPopup";
import ALMConnectionPage from "../../main-pages/input-pages/ALMConnection/ALMConnectionPage";
import MessagePopup from "../../popup-components/MessagePopup";
import { ReactComponent as UploadIcon } from "../../../images/uploadIcon.svg";
import * as XLSX from "xlsx";
import mammoth from "mammoth";
import UserStoriesUpload from "../user-story/UserStoriesUpload";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import ValidationPopup from "../../popup-components/ValidationPopup";
import UnrefinedUserStoriesPopup from "./UnrefinedUserStoriesPopup";
import { handleProgressBar } from "../../custom-components/ProgressBar/ProgressBarWithAPI";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const UserStoryFormInput = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [freeFlowUserStory, setFreeFlowUserStory] = useState("");
  const [openProjectPage, setOpenProjectPage] = useState(false);
  const [sessionExpiredValue, setSessionExpiredValue] = useState(false);
  const { setMessagePopup, setMessage } = useContext(MessagePopupContext);
  const [error, setError] = useState("");
  const [isValid, setIsValid] = useState(false);
  const { testCaseLoaderState, setTestCaseLoaderState } = useContext(
    TestCaseLoaderStateContext
  );
  const { setLoadingState } = useContext(LoadingModalContext);
  const isUserStoryFilled = freeFlowUserStory.trim();
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [uploadedUserStories, setUploadedUserStories] = useState([]);
  const [openUploadUserStory, setOpenUploadUserStory] = useState(false);
  const [openValidationPopup, setOpenValidationPopup] = useState(false);
  const [messageType, setMessageType] = useState("");
  const [openUnrefinedPopup, setOpenUnrefinedPopup] = useState("");
  const [unrefinedStoriesData, setUnrefinedStoriesData] = useState([]);
  const [enrichedStories, setEnrichedStories] = useState([]);

  const fileInputRef = useRef(null);

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleSubmitFreeFlowText = async () => {
    // setLoading(true)
    let progressHandler;

    setTestCaseLoaderState("Functional Specification Document");
    try {
      progressHandler = handleProgressBar({setLoadingState, totalDurationMs:9000});
      progressHandler.start("Validating the input");
      const validUserStory = await validCandidateUserStory({
        userStory: freeFlowUserStory,
      });

      const status = validUserStory.data?.Status;

      if (status !== "Yes, this is a user story candidate") {
        setMessageType("Invalid User Story");
        setOpenValidationPopup(true);

        return;
      }

      const userStorydata = await modifyUserStoryData(freeFlowUserStory);

      const enrichedStories = [];
      const unrefinedStories = [];

      for (const story of userStorydata.data) {
        const evaluation = await evaluateTestDesignReadiness({
          content: story,
        });

        if (evaluation.data?.test_design_ready === "Yes") {
          enrichedStories.push({ ...evaluation.data });
        } else {
          unrefinedStories.push({ ...evaluation.data });
        }
      }
      await progressHandler.finishAndClose();

      // Set state for unrefined stories
      setUnrefinedStoriesData(unrefinedStories);
      setEnrichedStories(enrichedStories);

      if (enrichedStories.length > 0 || unrefinedStories.length > 0) {
        // Only open this if no enriched stories are found
        setOpenUnrefinedPopup(true);
      }

      if (enrichedStories.length === 0 && unrefinedStories.length === 0) {
        setMessageType("Error");
        setOpenValidationPopup(true);
      }
      // setLoading(false);

      // const response = await generateFSD({
      //   userStory: enrichedStories,
      //   unrefinedStories,
      // });

      // navigate("/dashboard/generate-testcases/output", {
      //   state: {
      //     userStory: freeFlowUserStory,
      //     functionalSpecDocument: response.data.functionalSpec,
      //   },
      // });
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.response?.data?.Error ||
        "Something went wrong. Please try again.";

      setMessagePopup(true);
      setMessage(errorMsg);

      if (errorMsg === "Your session has expired.") {
        setSessionExpiredValue(true);
      }
      setLoadingState({ open: false, message: "", progress: null });
    } finally {
      setTestCaseLoaderState(null);
      if(progressHandler){
        progressHandler.stop();
      }
      setLoadingState({ open: false, message: "", progress: null });
    }
  };

  const importUserStoriesFormJira = async () => {
    setOpenProjectPage(true);
  };

  const validateInput = (input) => {
    const hasLetters = /[a-zA-Z]/.test(input);
    const hasOnlySpecialOrNumbers = /^[^a-zA-Z]*$/.test(input);

    if (!input) {
      setError("");
      setIsValid(false);
    } else if (!hasLetters || hasOnlySpecialOrNumbers) {
      setError("Invalid input! Please enter a meaningful user story.");
      setIsValid(false);
    } else {
      setError("");
      setIsValid(true);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setFreeFlowUserStory(value);
    validateInput(value);
  };

  const handleFileUpload = (event) => {
    event.preventDefault();
    setUploadedUserStories([]);
    setOpenUploadUserStory(false);
    setLoadingState({
      open: true,
      message: "Uploading your input file",
      progress: null,
    });

    const file = event.target.files[0];
    if (!file) return;

    const sizeInMB = file.size / (1024 * 1024);
    if (sizeInMB > 50) {
      setOpenValidationPopup(true);
      setMessageType("File Size");
      setLoadingState({ open: false, message: "", progress: null });
      return;
    }

    const extension = file.name.split(".").pop().toLowerCase();
    const reader = new FileReader();

    reader.onload = async (e) => {
      const arrayBuffer = e.target.result;

      try {
        switch (extension) {
          case "xlsx":
          case "xls":
            await handleExcelUpload(arrayBuffer);
            break;

          case "docx":
            await handleDocxUpload(arrayBuffer);
            break;

          case "pdf":
            await handlePdfUpload(arrayBuffer);
            break;

          default:
            setOpenValidationPopup(true);
            setMessageType("Unsupported format");
        }
      } catch (err) {
        setMessageType("Error");
        setOpenValidationPopup(true);
      } finally {
        event.target.value = null;
        setLoadingState({ open: false, message: "", progress: null });
      }
    };

    reader.readAsArrayBuffer(file);
  };

  // Excel Handler - Read all sheet
  const handleExcelUpload = async (arrayBuffer) => {
    try {
      const data = new Uint8Array(arrayBuffer);
      const workbook = XLSX.read(data, { type: "array" });

      let allSheetData = [];

      workbook.SheetNames.forEach((sheetName) => {
        const sheet = workbook.Sheets[sheetName];
        const sheetRows = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        if (sheetRows.length === 0) return;

        const [headerRow, ...dataRows] = sheetRows;

        // Check if first row looks like headers (e.g., contains "description")
        const structuredKeys = [
          "description",
          "title",
          "acceptance criteria",
          "additional information",
        ];

        const looksStructured = headerRow.some(
          (cell) =>
            typeof cell === "string" &&
            structuredKeys.some((key) => cell.toLowerCase().includes(key))
        );

        if (looksStructured) {
          // Normalize headers
          const headers = headerRow.map((h) =>
            typeof h === "string"
              ? h.trim().toLowerCase().replace(/\s+/g, "_")
              : ""
          );

          dataRows.forEach((row) => {
            const isEmptyRow = row.every(
              (cell) => !cell || String(cell).trim() === ""
            );
            if (isEmptyRow) return;

            const rowObject = {};
            headers.forEach((key, idx) => {
              rowObject[key] = row[idx] ? String(row[idx]).trim() : "";
            });

            const hasStructuredFields =
              rowObject["description"] || rowObject["acceptance_criteria"];

            if (hasStructuredFields) {
              allSheetData.push(rowObject);
            } else {
              const fullText = row.filter(Boolean).join(" ").trim();
              if (fullText) {
                allSheetData.push({ userStory: fullText });
              }
            }
          });
        } else {
          // Fallback for fully unstructured sheet
          sheetRows.forEach((row) => {
            const fullText = row.filter(Boolean).join(" ").trim();
            if (fullText) {
              allSheetData.push({ userStory: fullText });
            }
          });
        }
      });

      if (allSheetData.length === 0) {
        setMessageType("Invalid User Story");
        setOpenValidationPopup(true);
        return;
      }

      const plainText = allSheetData
        .map((obj) => Object.values(obj).join("\n"))
        .join("\n\n");

      console.log("plainText===>", plainText);

      const userStorydata = await modifyUserStoryData(plainText);

      const enrichedStories = [];
      const unrefinedStories = [];

      for (const story of userStorydata.data) {
        const evaluation = await evaluateTestDesignReadiness({
          content: story,
        });

        if (evaluation.data?.test_design_ready === "Yes") {
          enrichedStories.push({ ...evaluation.data });
        } else {
          unrefinedStories.push({ ...evaluation.data });
        }
      }

      setUnrefinedStoriesData(unrefinedStories);
      setEnrichedStories(enrichedStories);

      if (enrichedStories.length > 0 || unrefinedStories.length > 0) {
        setOpenUnrefinedPopup(true);
      }

      if (enrichedStories.length === 0 && unrefinedStories.length === 0) {
        setMessageType("Error");
        setOpenValidationPopup(true);
      }
    } catch (error) {
      console.error("Excel Upload Error:", error);
      setMessageType("Error");
      setOpenValidationPopup(true);
    }
  };

  // DOCX Handler
  const handleDocxUpload = async (arrayBuffer) => {
    try {
      const { value: rawText } = await mammoth.extractRawText({ arrayBuffer });

      const userStorydata = await modifyUserStoryData(rawText);

      const enrichedStories = [];
      const unrefinedStories = [];

      for (const story of userStorydata.data) {
        const evaluation = await evaluateTestDesignReadiness({
          content: story,
        });

        if (evaluation.data?.test_design_ready === "Yes") {
          enrichedStories.push({ ...evaluation.data });
        } else {
          unrefinedStories.push({ ...evaluation.data });
        }
      }

      // Set state for unrefined stories
      setUnrefinedStoriesData(unrefinedStories);
      setEnrichedStories(enrichedStories);

      if (enrichedStories.length > 0 || unrefinedStories.length > 0) {
        // Only open this if no enriched stories are found
        setOpenUnrefinedPopup(true);
      }

      if (enrichedStories.length === 0 && unrefinedStories.length === 0) {
        setMessageType("Error");
        setOpenValidationPopup(true);
      }
    } catch (error) {
      console.error("DOCX Upload Error:", error);
      setMessageType("Error");
      setOpenValidationPopup(true);
    }
  };

  //PDF Handler
  const handlePdfUpload = async (arrayBuffer) => {
    try {
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let fullText = "";

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();

        const pageText = textContent.items.map((item) => item.str).join(" ");
        fullText += pageText + "\n\n";
      }

      const userStorydata = await modifyUserStoryData(fullText);

      const enrichedStories = [];
      const unrefinedStories = [];

      for (const story of userStorydata.data) {
        const evaluation = await evaluateTestDesignReadiness({
          content: story,
        });

        if (evaluation.data?.test_design_ready === "Yes") {
          enrichedStories.push({ ...evaluation.data });
        } else {
          unrefinedStories.push({ ...evaluation.data });
        }
      }

      // Set state for unrefined stories
      setUnrefinedStoriesData(unrefinedStories);
      setEnrichedStories(enrichedStories);

      if (enrichedStories.length > 0 || unrefinedStories.length > 0) {
        // Only open this if no enriched stories are found
        setOpenUnrefinedPopup(true);
      }

      if (enrichedStories.length === 0 && unrefinedStories.length === 0) {
        setMessageType("Error");
        setOpenValidationPopup(true);
      }
    } catch (error) {
      console.error("PDF Upload Error:", error);
      setMessageType("Error");
      setOpenValidationPopup(true);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        bgcolor: "#f6f3f2",
        minHeight: "80vh",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "center",
        overflowY: "hidden",
        width: "100%",
        height: "100vh",
        position: "relative",
        WebkitOverflowScrolling: "touch",
        scrollbarWidth: "thin",
        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#888",
          borderRadius: "4px",
          minHeight: "20px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: "#555",
        },
      }}
    >
      <Box component="main" sx={{ flexGrow: 1, p: 2 }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              bgcolor: "transparent",
              marginBottom: 1,
              padding: 1,
              display: "flex",
              flexDirection: "column",
              marginRight: 1,
              ml: 2,
              width: { xs: '100%', md: 966, lg: 1100 },
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Box display="flex" alignItems="center" mb={0.1}>
                <AutomateIcon style={{ marginRight: 7, marginLeft: -8 }} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                  sx={{ mt: 0.5 }}
                >
                  Automate and create comprehensive, high-quality test cases
                  effortlessly
                </Typography>
              </Box>
              <Box sx={{ mr: 1 }}>
                <FullscreenToggle sx={{ fontSize: 32 }} />
              </Box>
            </Box>
          </Box>

          <Card
            sx={{
              borderRadius: "12px",
              width: { xs: '100%', md: 966, lg: 1100 },
              maxWidth: 'none',
              maxHeight: 'none',
              border: "1px solid rgba(0,0,0,0.12)",
              ml: 2
            }}
          >
            <form>
              <CardContent
                sx={{
                  padding: 1,
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <TextField
                  fullWidth
                  multiline
                  minRows={5}
                  maxRows={10}
                  margin="normal"
                  placeholder="Enter user story details"
                  value={freeFlowUserStory}
                  onChange={handleInputChange}
                  error={Boolean(error)}
                  helperText={error}
                  autoComplete="off"

                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "transparent",
                      padding: "10px 10px 10px 33px",
                      paddingRight: "10px",
                      zIndex: 1,
                      "& fieldset": {
                        border: "none",
                      },
                      "&:hover fieldset": {
                        border: "none",
                      },
                      "&.Mui-focused fieldset": {
                        border: "none",
                      },
                    },
                  }}
                />

                <hr
                  style={{
                    borderColor: "rgba(0, 0, 0, 0.12)",
                    width: "100%",
                    margin: "10px 0",
                  }}
                />

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    mt: 1,
                    width: "100%", // make sure it takes full width
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%", // space between only works if it fills the row
                    }}
                  >
                    {/* Left buttons */}
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <input
                        type="file"
                        id="file-upload-input"
                        ref={fileInputRef}
                        accept=".xlsx,.xls,.docx,.pdf"
                        style={{ display: "none" }}
                        onChange={handleFileUpload}
                      />

                      <Button
                        color="primary"
                        startIcon={<UploadIcon />}
                        sx={{ color: "#49A3FF", textTransform: "none" }}
                        onClick={handleUploadClick}
                      >
                        Upload
                      </Button>

                      <Button
                        color="primary"
                        startIcon={<ImportIcon />}
                        onClick={importUserStoriesFormJira}
                        sx={{ color: "#49A3FF", textTransform: "none" }}
                      >
                        Import
                      </Button>
                    </Box>

                    {/* Right button */}
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: "#49A3FF",
                        color: "white",
                        textTransform: "none",
                        mr:5
                      }}
                      disabled={
                        !isValid ||
                        !isUserStoryFilled ||
                        testCaseLoaderState === "Functional Specification Document"
                      }
                      onClick={handleSubmitFreeFlowText}
                    >
                      Analyze
                    </Button>
                  </Box>

                  {/* Supported formats text */}
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "Manrope",
                      fontWeight: "bold",
                      fontSize: "12px",
                      lineHeight: "24px",
                      letterSpacing: "0.15px",
                      color: "#000000",
                      mt: 1,
                      ml: 1,
                    }}
                  >
                    Supported formats for upload:
                    <Typography
                      component="span"
                      sx={{
                        display: "inline-block",
                        fontFamily: "Manrope",
                        fontWeight: 500,
                        fontSize: "12px",
                        color: "#4B4B4B",
                        ml: 0.5,
                      }}
                    >
                      .docx, .pdf, .xls, .xlsx, .jpg, .png, .mp3, .mp4, .wav, .avi
                    </Typography>
                  </Typography>
                </Box>

              </CardContent>
            </form>
          </Card>
        </Container>

        {openProjectPage && (
          <ALMConnectionPage
            issueType={"User Stories"}
            setPageCloser={setOpenProjectPage}
          />
        )}

        {openUploadUserStory && (
          <UserStoriesUpload uploadedUserStories={uploadedUserStories} />
        )}

        <UnrefinedUserStoriesPopup
          open={openUnrefinedPopup}
          onClose={() => setOpenUnrefinedPopup(false)}
          unrefinedStories={unrefinedStoriesData}
          enrichedStories={enrichedStories}
        />

        <ValidationPopup
          open={openValidationPopup}
          messageType={messageType}
          onClose={() => setOpenValidationPopup(false)}
        />
        {sessionExpiredValue && <SessionExpired />}
      </Box>
    </Box>
  );
};
export default UserStoryFormInput;
